/**
 * useNoiseFilter (web) — « Supprimer les bruits de fond », dans le choix du micro en pleine séance.
 *
 * L'interrupteur de l'ancien sélecteur (`MediaDeviceSelectM`) : le filtre Krisp de LiveKit, proposé
 * seulement quand le navigateur le permet, jamais allumé d'office (son calcul pèse sur les petits
 * ordinateurs ; la suppression de bruit du navigateur reste active sans lui).
 *
 * Il pose lui-même le processeur sur la piste du micro, au lieu du `useKrispNoiseFilter` de LiveKit,
 * pour deux raisons :
 *  - **l'état affiché est celui de la piste** (`getProcessor()`, `isEnabled()`), pas un souvenir du
 *    crochet : le crochet de LiveKit repart éteint à chaque montage alors que la piste reste filtrée ;
 *  - **un échec à poser le filtre part dans Bugsnag** : celui de LiveKit ne le confie qu'à sa console.
 *
 * Monté pendant tout le live (par l'écran), comme l'ancien sélecteur qui restait monté : un filtre
 * demandé avant que la piste du micro n'existe se pose à sa publication.
 */
import { useMaybeRoomContext } from "@livekit/components-react"
import { RoomEvent, Track, type LocalAudioTrack, type Room } from "livekit-client"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { bugsnagClient } from "../../shared/bugsnagClient"
import { type EngineTelemetry } from "../../shared/telemetry"
import { type NoiseFilter } from "./roomDevices"

type TrackEventName = Parameters<LocalAudioTrack["on"]>[0]

/**
 * Ce que la piste du micro dit de son filtre (`TrackEvent.TrackProcessorUpdate`,
 * `AudioTrackFeatureUpdate`, `Restarted`) : l'état se relit à chacun.
 */
export const TRACK_EVENTS: readonly TrackEventName[] = ["trackProcessorUpdate", "audioTrackFeatureUpdate", "restarted"]

/** Le nom que Krisp donne à son processeur (`KrispNoiseFilterProcessor.name`). */
const KRISP_PROCESSOR = "livekit-noise-filter"

type KrispProcessor = { name: string; isEnabled: () => boolean; setEnabled: (enabled: boolean) => Promise<unknown> }

/** Ce qu'on lit du module de Krisp : un test passe le sien. */
export type KrispModule = {
    isKrispNoiseFilterSupported: () => boolean
    KrispNoiseFilter: () => unknown
}

export const loadKrisp = (): Promise<KrispModule> =>
    import("@livekit/krisp-noise-filter") as unknown as Promise<KrispModule>

function micTrackOf(room: Room | undefined): LocalAudioTrack | null {
    const track = room?.localParticipant?.getTrackPublication(Track.Source.Microphone)?.track as
        LocalAudioTrack | undefined
    return track != null && typeof track.getProcessor === "function" ? track : null
}

function krispOf(track: LocalAudioTrack | null): KrispProcessor | null {
    const processor = track?.getProcessor() as unknown as KrispProcessor | undefined
    return processor?.name === KRISP_PROCESSOR ? processor : null
}

/** Le filtre tourne-t-il vraiment sur la piste du micro ? */
export function noiseFilterApplied(track: LocalAudioTrack | null): boolean {
    return krispOf(track)?.isEnabled() === true
}

async function applyFilter(track: LocalAudioTrack, enabled: boolean, krisp: () => Promise<KrispModule>) {
    const current = krispOf(track)
    if (current != null) {
        await current.setEnabled(enabled)
        return
    }
    if (!enabled) return
    const module = await krisp()
    if (!module.isKrispNoiseFilterSupported()) return
    const processor = module.KrispNoiseFilter() as KrispProcessor
    await track.setProcessor(processor as never)
    await processor.setEnabled(true)
}

export function useNoiseFilter(
    telemetry: EngineTelemetry,
    krisp: () => Promise<KrispModule> = loadKrisp
): NoiseFilter | null {
    const room = useMaybeRoomContext()
    const [supported, setSupported] = useState(false)
    const [enabled, setEnabledState] = useState(() => noiseFilterApplied(micTrackOf(room)))
    const [pending, setPending] = useState(false)
    // Ce que l'élève a demandé : posé à la publication du micro s'il n'existait pas encore.
    const wantedRef = useRef(false)
    const telemetryRef = useRef(telemetry)
    telemetryRef.current = telemetry
    const krispRef = useRef(krisp)
    // Une application à la fois : la suivante attend la précédente. Deux en même temps (un double
    // toucher, la publication du micro pendant une demande) voyaient toutes deux la piste sans filtre,
    // et posaient chacune son processeur.
    const chainRef = useRef<Promise<void>>(Promise.resolve())
    const inFlightRef = useRef(0)

    useEffect(() => {
        let mounted = true
        krispRef
            .current()
            .then((module) => {
                if (mounted) setSupported(module.isKrispNoiseFilterSupported())
            })
            .catch((cause: unknown) => {
                // Le module ne se charge pas : le navigateur ne le permet pas. Pas d'interrupteur.
                bugsnagClient.leaveBreadcrumb("live: noise filter unavailable", {
                    reason: cause instanceof Error ? cause.name : typeof cause,
                })
            })
        return () => {
            mounted = false
        }
    }, [])

    const apply = useCallback((track: LocalAudioTrack, next: boolean) => {
        inFlightRef.current += 1
        setPending(true)
        chainRef.current = chainRef.current
            .then(() => applyFilter(track, next, krispRef.current))
            .catch((cause: unknown) => {
                telemetryRef.current.error("noise_filter", cause, { enabled: next })
            })
            .finally(() => {
                inFlightRef.current -= 1
                if (inFlightRef.current === 0) setPending(false)
                setEnabledState(noiseFilterApplied(track))
            })
    }, [])

    // La piste du micro qui paraît ou disparaît : l'état se relit sur elle. Et ce que la piste dit
    // d'elle-même aussi : son processeur posé ou retiré, le filtre que Krisp allume ou coupe de son
    // côté, la piste relancée (un autre micro) — sans quoi l'interrupteur gardait son dernier état.
    useEffect(() => {
        if (room == null) return undefined
        let watched: LocalAudioTrack | null = null
        const readTrack = () => setEnabledState(noiseFilterApplied(micTrackOf(room)))
        const watch = (track: LocalAudioTrack | null) => {
            if (track === watched) return
            for (const event of TRACK_EVENTS) watched?.off?.(event, readTrack)
            watched = track
            for (const event of TRACK_EVENTS) watched?.on?.(event, readTrack)
        }
        const read = () => {
            const track = micTrackOf(room)
            watch(track)
            setEnabledState(noiseFilterApplied(track))
            if (track != null && wantedRef.current && krispOf(track) == null) apply(track, true)
        }
        read()
        room.on(RoomEvent.LocalTrackPublished, read)
        room.on(RoomEvent.LocalTrackUnpublished, read)
        return () => {
            room.off(RoomEvent.LocalTrackPublished, read)
            room.off(RoomEvent.LocalTrackUnpublished, read)
            watch(null)
        }
    }, [room, apply])

    const setEnabled = useCallback(
        (next: boolean) => {
            wantedRef.current = next
            const track = micTrackOf(room)
            if (track != null) apply(track, next)
        },
        [room, apply]
    )

    return useMemo(
        () => (supported ? { enabled, pending, setEnabled } : null),
        [supported, enabled, pending, setEnabled]
    )
}
